import { CompiledBehaviorPlanSchema } from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * ERP Apps: agent-built pages. An App's active deployment holds a manifest
 * (json-render view + named tool calls); the backend runs the calls as the
 * viewer, or as the App's dedicated agent when it was set up with one,
 * repairing failed calls where it can.
 *
 * A Report (profile `report`) is a read-only, filterable App over one Workbook
 * Report or App Output: no agent, no commands. Its workbook feeds are read as the viewer, under
 * their Workbook access (`openReport`, `readReport`).
 */

// --- Response schemas ---

/**
 * Who an App's tool calls run as: the viewer, under their own permissions, or
 * the dedicated agent (a service account) it was set up with.
 */
export const AppRunAsSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("viewer") }),
  z.object({ kind: z.literal("agent"), agentDefinitionId: z.uuid() }),
]);

/** An interactive App. Reports are `ReportAppSchema`; Apps endpoints never return one. */
export const AppSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** The backing agent: the assistant's when the App runs as its viewer. */
  agentDefinitionId: z.uuid(),
  runAs: AppRunAsSchema,
  createdByUserId: z.uuid().nullable(),
  /** "Automatically fix errors and keep the App up to date with the latest Gently tools". */
  autoUpdateEnabled: z.boolean(),
  activeDeploymentId: z.uuid().nullable(),
});

/** A Report: no agent; reads one Workbook Report or App Output as the viewer. */
export const ReportAppSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  profile: z.literal("report"),
  workbookId: z.uuid(),
  workbookOutputId: z.uuid(),
  createdByUserId: z.uuid().nullable(),
  autoUpdateEnabled: z.boolean(),
  activeDeploymentId: z.uuid().nullable(),
});

export const AppDeploymentSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  status: z.enum(["draft", "active", "superseded"]),
  origin: z.enum(["author", "auto_repair"]),
  changeNote: z.string().nullable(),
  sourceSha256: z.string(),
  createdAt: z.coerce.date(),
});

export const AppToolRepairSchema = z.object({
  id: z.uuid(),
  deploymentId: z.uuid(),
  callSite: z.string(),
  toolType: z.string(),
  httpStatus: z.number().int().nullable(),
  errorMessage: z.string(),
  outcome: z.enum([
    "repaired",
    "unrepairable",
    "forbidden",
    "transient_exhausted",
    "failed",
  ]),
  explanation: z.string().nullable(),
  appliedDeploymentId: z.uuid().nullable(),
  createdAt: z.coerce.date(),
});

export const AppFeedErrorSchema = z.object({
  key: z.string(),
  toolType: z.string(),
  outcome: z.string(),
  message: z.string(),
});

/** A json-render spec in the Apps catalog; the renderer narrows it with `isAppViewSpec`. */
const AppViewSchema = z.object({
  root: z.string(),
  elements: z.record(z.string(), z.unknown()),
  state: z.record(z.string(), z.unknown()).optional(),
});

export const OpenedAppSchema = z.object({
  app: AppSchema,
  deployment: AppDeploymentSchema.pick({
    id: true,
    version: true,
    origin: true,
    createdAt: true,
  }),
  view: AppViewSchema,
  /** The command names the view may trigger; anything else is not the App's. */
  commands: z.array(z.string()).readonly(),
  /** Feed values by key; a failed key is null and listed in `errors`. */
  state: z.record(z.string(), z.unknown()),
  errors: z.array(AppFeedErrorSchema).readonly(),
  /**
   * The validated behavior plan for a manifest v2 App. Absent for v1, so a
   * client that predates behaviors keeps working and a client that supports
   * them knows when not to compose any.
   */
  behaviors: CompiledBehaviorPlanSchema.nullish(),
});

export const AppCommandResultSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("succeeded"),
    result: z.unknown(),
    state: z.record(z.string(), z.unknown()),
    errors: z.array(AppFeedErrorSchema).readonly(),
  }),
  z.object({
    status: z.literal("failed"),
    outcome: z.string(),
    message: z.string(),
  }),
]);

export const FeedValueSchema = z.object({
  fields: z.array(z.string()).readonly(),
  rows: z.array(z.record(z.string(), z.unknown())).readonly(),
  meta: z.object({
    rowCount: z.number().int().nonnegative(),
    truncated: z.boolean(),
  }),
});

/** One feed's read; one feed failing fails no other. */
export const WorkbookFeedStatusSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ready"), value: FeedValueSchema }),
  z.object({ status: z.literal("error"), message: z.string() }),
]);

const FeedsSchema = z.record(z.string(), WorkbookFeedStatusSchema);

export const OpenedReportSchema = z.object({
  app: ReportAppSchema,
  deployment: OpenedAppSchema.shape.deployment,
  view: AppViewSchema,
  /** The view state the feeds read, by JSON Pointer: send their values with every read. */
  inputs: z.array(z.string()).readonly(),
  feeds: FeedsSchema,
});

export const AppDetailsSchema = z.object({
  app: AppSchema,
  deployments: z.array(AppDeploymentSchema).readonly(),
  repairs: z.array(AppToolRepairSchema).readonly(),
});

export type App = z.infer<typeof AppSchema>;
export type ReportApp = z.infer<typeof ReportAppSchema>;
export type AppDeployment = z.infer<typeof AppDeploymentSchema>;
export type AppToolRepair = z.infer<typeof AppToolRepairSchema>;
export type OpenedApp = z.infer<typeof OpenedAppSchema>;
export type AppCommandResult = z.infer<typeof AppCommandResultSchema>;
export type AppDetails = z.infer<typeof AppDetailsSchema>;
export type WorkbookFeedStatus = z.infer<typeof WorkbookFeedStatusSchema>;
export type OpenedReport = z.infer<typeof OpenedReportSchema>;

// --- Input schemas ---

const AppIdInputSchema = z.object({ appId: z.uuid() });

/** An App manifest; validated in full by the backend (`AppManifestSchema`). */
const AppManifestInputSchema = z.record(z.string(), z.unknown());

export const CreateAppInputSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "Lowercase letters, digits and dashes"),
  name: z.string().min(1).max(160),
  description: z.string().max(2000).nullable().optional(),
  /**
   * A background agent for the App to run as (its service account). Without
   * one, the App runs as its viewer.
   */
  agentDefinitionId: z.uuid().optional(),
  manifest: AppManifestInputSchema,
});

export const CreateReportInputSchema = CreateAppInputSchema.omit({
  agentDefinitionId: true,
}).extend({
  workbookId: z.uuid(),
  /** A Report or App Output of the Workbook; it has at most one Report App. */
  workbookOutputId: z.uuid(),
});

/** Values of the view state a Report's feeds read, keyed by the pointers `openReport` lists. */
const FeedInputsSchema = z.record(z.string().max(256), z.unknown());

// --- Contracts ---

export const appsContracts = {
  list: oc.input(z.object({})).output(z.array(AppSchema).readonly()).route({
    summary: "List Apps",
    description: "Lists the organization's Apps.",
    method: "GET",
  }),

  get: oc.input(AppIdInputSchema).output(AppDetailsSchema).route({
    summary: "Get an App",
    description:
      "Returns one App with its deployments (newest first) and its most recent tool-call repairs.",
    method: "GET",
  }),

  open: oc
    .input(
      AppIdInputSchema.extend({
        /**
         * The newest behavior plan this client can run. Omitted by a client
         * that predates behaviors, which a v2 App refuses rather than mounting
         * a view it cannot compose.
         */
        supportedBehaviorPlanVersion: z.number().int().nonnegative().default(0),
      }),
    )
    .output(OpenedAppSchema)
    .route({
      summary: "Open an App",
      description:
        "Runs the active deployment's feeds (tool feeds as the App runs, workbook and stateful-table feeds as the caller) and returns the view with its state, plus the behavior plan a v2 App runs. A failed feed is reported per key rather than failing the request.",
      method: "GET",
    }),

  runCommand: oc
    .input(
      AppIdInputSchema.extend({
        /** The deployment whose view sent the command (from `open`). */
        deploymentId: z.uuid(),
        command: z.string().min(1).max(64),
        params: z.record(z.string(), z.unknown()).default({}),
      }),
    )
    .output(AppCommandResultSchema)
    .route({
      summary: "Run an App command",
      description:
        "Runs a named command's tool call, then re-runs the feeds it refreshes. A tool failure the repair loop could not fix returns status `failed`. Refused when `deploymentId` is no longer the active version, so a stale page reloads instead of sending old commands to a new version.",
      method: "POST",
    }),

  openReport: oc
    .input(AppIdInputSchema.extend({ inputs: FeedInputsSchema.default({}) }))
    .output(OpenedReportSchema)
    .route({
      summary: "Open a Report",
      description:
        "Returns a Report's view and reads its feeds as the caller, under their Workbook access, narrowed by `inputs`. A feed that can't be read is reported per key rather than failing the request.",
      method: "POST",
    }),

  readReport: oc
    .input(
      AppIdInputSchema.extend({
        /** The deployment whose view sent the read (from `openReport`). */
        deploymentId: z.uuid(),
        inputs: FeedInputsSchema.default({}),
      }),
    )
    .output(z.object({ feeds: FeedsSchema }))
    .route({
      summary: "Read a Report's feeds",
      description:
        "Reads every feed again with new filter `inputs`, as the caller. Refused when `deploymentId` is no longer the active version.",
      method: "POST",
    }),

  createReport: oc
    .input(CreateReportInputSchema)
    .output(ReportAppSchema)
    .route({
      summary: "Create a Report",
      description:
        "Creates a Report App over one of the caller's Workbook Report or App Outputs and deploys its first version as active; when the Output already has one, deploys this manifest as its next active version and returns it. Needs Workbook edit access, not an agent.",
      method: "POST",
    }),

  setAutoUpdate: oc
    .input(AppIdInputSchema.extend({ enabled: z.boolean() }))
    .output(AppSchema)
    .route({
      summary: "Set automatic fixes for an App",
      description:
        'Turns "Automatically fix errors and keep the App up to date with the latest Gently tools" on or off.',
      method: "POST",
    }),

  create: oc.input(CreateAppInputSchema).output(AppSchema).route({
    summary: "Create an App",
    description:
      "Creates an App and deploys its first version as active. It runs as its viewer, or as `agentDefinitionId` when given.",
    method: "POST",
  }),

  deploy: oc
    .input(
      AppIdInputSchema.extend({
        manifest: AppManifestInputSchema,
        changeNote: z.string().max(2000).nullable().optional(),
        activate: z.boolean().default(true),
      }),
    )
    .output(AppDeploymentSchema)
    .route({
      summary: "Deploy an App version",
      description:
        "Validates a manifest, stores its source and records the next version, activating it unless `activate` is false.",
      method: "POST",
    }),

  activate: oc
    .input(AppIdInputSchema.extend({ deploymentId: z.uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Activate an App version",
      description:
        "Makes an earlier or draft deployment the App's active version (rollback or promote).",
      method: "POST",
    }),
};
